import { yupResolver } from "@hookform/resolvers/yup";
import { endOfMonth, isBefore, isSameDay } from "date-fns";
import { t } from "i18next";
import CommonProjectInvoiceDetails from "modules/billing/components/CommonProjectInvoiceDetails";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import useAsyncEffect from "use-async-effect";
import * as yup from "yup";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormInputError from "~/components/formElements/FormInputError";
import SubmitButton from "~/components/formElements/SubmitButton";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import {
	getPendingInvoiceStatusReport,
	updatePendingRegularInvoiceStatusToDraft,
} from "~/modules/billing/api/pendingInvoice/pendingInvoiceApiDispatchers.ts";
import type { UnprocessedInvoiceStatusReportData } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import type { UnprocessedInvoicesIndexTableData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import type { User, UserWithConfidentialInformation } from "~/modules/user/api/user/userTypes.ts";
import { formatDateToYYYYMMDD, formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

import InvoiceDetailsElement from "../../../../../InvoiceDetailsElement";

type FormData = {
	deliveryStartDate: Date;
	deliveryEndDate: Date;
}

type Props = {
	allUsersData: User[] | UserWithConfidentialInformation[];
	onCancel: () => void;
	onSuccess: () => void;
	pendingInvoiceData: UnprocessedInvoicesIndexTableData;
};

const endOfCurrentMonth = endOfMonth(new Date());

const StartRegularInvoiceBillingProcessForm = ({
	allUsersData,
	onCancel,
	onSuccess,
	pendingInvoiceData,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [isPerformingPreCheck, setIsPerformingPreCheck] = useState<boolean>(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const [updatePreCheckData, setUpdatePreCheckData] = useState<UnprocessedInvoiceStatusReportData | null>(null);

	const schema = useMemo(() => {
		return yup.object({
			deliveryStartDate: yup.date().required(),
			deliveryEndDate: yup
				.date()
				.required()
				.min(yup.ref("deliveryStartDate"), "Das Startdatum muss vor dem Enddatum liegen.")
				.default(null),
			invoiceTypeId: yup.string().required(),
			invoiceNumber: yup.string().default("")
				.when("invoiceTypeId", {
					is: invoiceTypeId.SelfBilledInvoice,
					then: (schema) => schema.required(),
					otherwise: (schema) => schema.nullable().default(null),
				}),
		});
	}, []);

	const defaultValues = useMemo(() => {
		return {
			deliveryStartDate: pendingInvoiceData.deliveryStartDate,
			deliveryEndDate: pendingInvoiceData.deliveryEndDate,
			invoiceTypeId: pendingInvoiceData.invoiceTypeId,
		};
	}, [pendingInvoiceData.deliveryEndDate, pendingInvoiceData.deliveryStartDate, pendingInvoiceData.invoiceTypeId]);

	const {
		handleSubmit,
		control,
		formState: { isSubmitted, isValid },
		watch,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
		mode: "onChange",
	});

	const deliveryStartDate = watch("deliveryStartDate");
	const deliveryEndDate = watch("deliveryEndDate");
	const dateChanged = !isSameDay(deliveryStartDate, pendingInvoiceData.deliveryStartDate) || !isSameDay(deliveryEndDate, pendingInvoiceData.deliveryEndDate);

	useAsyncEffect(async (isMounted) => {
		if (!dateChanged) {
			setUpdatePreCheckData(null);
			setIsPerformingPreCheck(false);
			return;
		}
		if (isBefore(deliveryEndDate, deliveryStartDate)) return;

		setIsPerformingPreCheck(true);
		const startDate = formatDateToYYYYMMDD(deliveryStartDate);
		const endDate = formatDateToYYYYMMDD(deliveryEndDate);
		const preCheckResponse = await getPendingInvoiceStatusReport(pendingInvoiceData.id, startDate, endDate);
		if (!isMounted()) return;
		setUpdatePreCheckData(getDataFromResponse(preCheckResponse));
		setIsPerformingPreCheck(false);
	}, [dateChanged, deliveryEndDate, deliveryStartDate]);

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			const updateData = {
				deliveryEndDate: formatDateToYYYYMMDD(data.deliveryEndDate),
				deliveryStartDate: formatDateToYYYYMMDD(data.deliveryStartDate),
			};

			await updatePendingRegularInvoiceStatusToDraft(pendingInvoiceData.id, updateData);
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
			setIsBusy(false);
		}
	}, [onSuccess, pendingInvoiceData.id]);

	const hasConflicts = updatePreCheckData?.conflicts && updatePreCheckData.conflicts.length > 0;
	const invoiceTotalIsInvalid = updatePreCheckData?.invoiceTotalCents === 0;
	const formIsSubmittable = !hasConflicts && !invoiceTotalIsInvalid && !isPerformingPreCheck && isValid && !isBusy;

	const combinedInvoiceDataWithPreCheckData = useMemo(() => {
		if (updatePreCheckData) {
			return {
				...pendingInvoiceData,
				allStaffingsClosed: updatePreCheckData.allStaffingsClosed,
				invoiceTotalCents: updatePreCheckData.invoiceTotalCents,
				hasFreelancers: updatePreCheckData.hasFreelancers,

			};
		}
		return pendingInvoiceData;
	}, [pendingInvoiceData, updatePreCheckData]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Abrechnungsvorgang starten</SidebarHeaderHeadline>
			</SidebarHeader>
			<div className="grow overflow-auto px-7 py-2">
				{(isBusy || isPerformingPreCheck) && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				{pendingInvoiceData.hasOverbookedOrder && <><Hint size="sm"
																  theme="error">Das Kostenvolumen der Bestellung ist
					überbucht.</Hint>
					<div className="h-2" />
				</>}

				<CommonProjectInvoiceDetails allUsersData={allUsersData}
											 invoiceData={combinedInvoiceDataWithPreCheckData}
											 staffingsClosingStatuses={updatePreCheckData ? updatePreCheckData.staffingsClosingStatuses : pendingInvoiceData.staffingsClosingStatuses}
											 showClosingStatusError={!combinedInvoiceDataWithPreCheckData.allStaffingsClosed}
				>
					<>
						<InvoiceDetailsElement label="Leistungszeitraum anpassen">
							<div className="grid w-full grid-cols-2 gap-x-4">
								<DatePickerInput name="deliveryStartDate"
												 clearable={false}
												 control={control}
												 label="Start" />

								<DatePickerInput name="deliveryEndDate"
												 clearable={false}
												 control={control}
												 maxDate={endOfCurrentMonth}
												 label="Ende" />
								{isValid && !isPerformingPreCheck && hasConflicts && <div className="col-span-2">
									<FormInputError>In dem ausgewählten Leistungszeitraum existieren bereits Rechnungen
										für
										diese Bestellung:</FormInputError>
									{updatePreCheckData?.conflicts.map((conflict, index) => (
										<div key={index}
											 className="my-2 flex w-full items-center justify-between rounded border border-red-500 p-2 text-sm text-red-500">
											<div>{conflict.invoiceNumber}</div>
											<div>{formatSmartDateRange(new Date(conflict.deliveryStartDate), new Date(conflict.deliveryEndDate), {
												month: "MMMM",
												year: " yyyy",
											})}</div>
										</div>
									))}
								</div>}
								{isValid && !isPerformingPreCheck && invoiceTotalIsInvalid &&
									<div className="col-span-2">
										<FormInputError>In diesem Zeitraum liegen keine abrechenbaren Erfassungen
											vor.</FormInputError>
									</div>}
							</div>
						</InvoiceDetailsElement>
					</>
				</CommonProjectInvoiceDetails>
			</div>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && (!isValid || !combinedInvoiceDataWithPreCheckData.allStaffingsClosed)}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable || !combinedInvoiceDataWithPreCheckData.allStaffingsClosed}>
					Rechnungsstatus auf &quot;In Arbeit&quot; ändern
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default StartRegularInvoiceBillingProcessForm;
